import React from 'react';
import { Route, Redirect } from 'react-router-dom';
import { isEmpty } from 'lodash';

import routes from '../../routes';
import rewardRoutes from '../../routes/rewards';
import orderingRoutes from '../../routes/ordering';
import pagesRoutes from '../../routes/pages';
import { FUDR_SURFACES } from '../../utils/fudrBoot';
import { cashCardFeatureEnabled } from '../../constants/globals';

/**
 * FUDR-14577 — hardcoded route sets per surface.
 * Engage/legacy containers stay in the repo; only registration is gated.
 */

/** Shared always-on: /pages + PG / redirect helpers. */
export const renderSharedRoutes = ({ Pages, Payment, URLRedirectHandler, CustomLinkRedirect }) => [
  <Route
    key="pages"
    path={`/${pagesRoutes.pages}`}
    render={(props) => <Pages {...props} />}
  />,
  <Route key="payment" path={`/${routes.containers.payment}`} component={Payment} />,
  <Route
    key="redirect"
    exact
    path={`/${routes.containers.redirect}`}
    component={URLRedirectHandler}
  />,
  <Route
    key="custom"
    exact
    path={`/${routes.containers.custom}/:customLinkKey`}
    component={CustomLinkRedirect}
  />,
];

export const renderOrderingStandaloneRoute = ({ Ordering }) => (
  <Route
    key="ordering-shell"
    path={`/${orderingRoutes.ordering}`}
    render={(props) => <Ordering key="ordering-shell" {...props} showWebElements={true} />}
  />
);

/**
 * Fudrgram / IG deep links stay on bare /view/* (not /app/view). Must be
 * registered on every surface that can resolve for a whitelabel host — APP and
 * SITE — or the default Redirect / Site shell swallows /view/ig → /app or home.
 */
export const renderViewRoute = ({ View }) => (
  <Route
    key="view"
    path={`/${routes.containers.view}`}
    render={(props) => <View {...props} />}
  />
);

const renderDefaultRoute = ({ contextualHome, DefaultPage }) =>
  isEmpty(contextualHome) ? (
    <Route key="default" path="/" component={DefaultPage} />
  ) : (
    <Redirect key="default" to={contextualHome} />
  );

/**
 * Browser + website: /app/scan stays as a QR deep-link. Other /app/* and /site/*
 * fall through to the Site shell so pageGraph.baseRedirects can rewrite them
 * before matching (contract: apply baseRedirects before route matching).
 *
 * Do not mount the standalone /ordering shell here — it would win over Site and
 * skip /ordering → /order (and other) prefix rewrites. Ordering on website lives
 * under Site at /order (or the graph-published path).
 */
export const renderSiteSurfaceRoutes = (ctx) => {
  const { Site, RewardsScan } = ctx;
  const appRoot = `/${rewardRoutes.rewards}`;
  const appScanPath = `${appRoot}/${rewardRoutes.containers.scan}`;

  return [
    ...renderSharedRoutes(ctx),
    <Route
      key="site-app-scan"
      path={`${appScanPath}/:scanCode?`}
      render={(props) => <RewardsScan key="site-app-scan" {...props} />}
    />,
    // Before Site path="/" — otherwise /view/ig never reaches View.
    renderViewRoute(ctx),
    <Route
      key="site-shell"
      path="/"
      render={(props) => <Site key="site-shell" {...props} />}
    />,
    renderDefaultRoute(ctx),
  ];
};

export const renderAppSurfaceRoutes = (ctx) => {
  const {
    Rewards,
    Wallet,
    showPWAFeatureFlag,
    DownloadApp,
    handleInstallPWAClick,
    enablePWAInstallWidgets,
    isPWAInstalled,
    pwaInstallUserChoice,
    isPWAInstallable,
  } = ctx;
  return [
    ...renderSharedRoutes(ctx),
    renderOrderingStandaloneRoute(ctx),
    // Before default Redirect to /app — otherwise /view/ig always bounces home.
    renderViewRoute(ctx),
    <Route
      key="app-shell"
      path={`/${rewardRoutes.rewards}`}
      render={(props) => <Rewards key="app-shell" {...props} />}
    />,
    <Route
      key="wallet-root"
      path={`/${rewardRoutes.containers.wallet}`}
      render={(props) => <Wallet {...props} />}
    />,
    showPWAFeatureFlag && (
      <Route
        key="get-app"
        path={`/${routes.containers.getApp}`}
        render={(props) => (
          <DownloadApp
            {...props}
            handleInstallPWAClick={handleInstallPWAClick}
            enablePWAInstallWidgets={enablePWAInstallWidgets}
            isPWAInstalled={isPWAInstalled}
            pwaInstallUserChoice={pwaInstallUserChoice}
            isPWAInstallable={isPWAInstallable}
          />
        )}
      />
    ),
    renderDefaultRoute(ctx),
  ].filter(Boolean);
};

export const renderOrderingSurfaceRoutes = (ctx) => [
  ...renderSharedRoutes(ctx),
  renderOrderingStandaloneRoute(ctx),
  renderDefaultRoute(ctx),
];

export const renderLegacySurfaceRoutes = (ctx) => {
  const {
    Restaurant,
    Cart,
    Confirmation,
    OrderList,
    Account,
    Offers,
    AboutFudr,
    AboutOutlet,
    TermsAndConditions,
    PrivacyPolicy,
    HelpAndSupport,
    CashCards,
    Deals,
    CouponDetail,
    DealDetail,
    Feedback,
    RewardsClubSignup,
    View,
    WelcomeScreen,
    DownloadApp,
    resolveScanRouteComponent,
    showDesktopLandingScreen,
    redirectToOrderingSite,
    showCashCardFeatureOnScanScreenFlag,
    enablePWAInstallWidgets,
    handleInstallPWAClick,
    showPWAFeatureFlag,
    showCashCardFeatureFlag,
    isRestaurantEntityGroup,
    renderGreetingSplashScreenComponent,
    isPWAInstalled,
    pwaInstallUserChoice,
    isPWAInstallable,
  } = ctx;

  return [
    ...renderSharedRoutes(ctx),
    <Route
      key="scan"
      path={`/${routes.containers.scan}`}
      render={(props) =>
        resolveScanRouteComponent({
          props,
          showDesktopLandingScreen,
          redirectToOrderingSite,
          showCashCardFeatureOnScanScreenFlag,
        })
      }
    />,
    <Route
      key="restaurant"
      path={`/${routes.containers.restaurant}/:restaurantAndTableId`}
      render={(props) => (
        <Restaurant
          {...props}
          enablePWAInstallWidgets={enablePWAInstallWidgets}
          handleInstallPWAClick={handleInstallPWAClick}
          pwaFeatureEnabled={showPWAFeatureFlag}
          cashCardFeatureEnabled={showCashCardFeatureFlag}
        />
      )}
    />,
    <Route key="cart" path={`/${routes.containers.cart}`} component={Cart} />,
    <Route
      key="confirmation"
      path={`/${routes.containers.confirmation}/:orderId`}
      render={(props) => (
        <Confirmation
          {...props}
          enablePWAInstallWidgets={enablePWAInstallWidgets}
          handleInstallPWAClick={handleInstallPWAClick}
          pwaFeatureEnabled={showPWAFeatureFlag}
        />
      )}
    />,
    <Route
      key="orders"
      path={`/${routes.containers.orders}`}
      render={(props) => (
        <OrderList {...props} isRestaurantEntityGroup={isRestaurantEntityGroup} />
      )}
    />,
    cashCardFeatureEnabled && (
      <Route
        key="cash-cards"
        path={`/${routes.containers.cashCards}`}
        render={(props) => (
          <CashCards {...props} isRestaurantEntityGroup={isRestaurantEntityGroup} />
        )}
      />
    ),
    <Route key="account" path={`/${routes.containers.account}`} component={Account} />,
    <Route
      key="welcome"
      path={`/${routes.containers.welcome}/:restaurantAndTableId`}
      render={(props) => (
        <WelcomeScreen
          {...props}
          greetingSplashScreen={renderGreetingSplashScreenComponent(routes.containers.welcome)}
        />
      )}
    />,
    <Route key="offers" path={`/${routes.containers.offers}`} component={Offers} />,
    <Route key="about-fudr" path={`/${routes.containers.aboutFudr}`} component={AboutFudr} />,
    <Route
      key="about-outlet"
      path={`/${routes.containers.aboutOutlet}/:restaurantId`}
      component={AboutOutlet}
    />,
    <Route
      key="terms"
      path={`/${routes.containers.termsAndConditions}`}
      component={TermsAndConditions}
    />,
    <Route
      key="privacy"
      path={`/${routes.containers.privacyPolicy}`}
      component={PrivacyPolicy}
    />,
    <Route
      key="help"
      path={`/${routes.containers.helpAndSupport}`}
      component={HelpAndSupport}
    />,
    <Route
      key="deals"
      path={`/${routes.containers.deals}`}
      render={(props) => (
        <Deals {...props} greetingSplashScreen={renderGreetingSplashScreenComponent()} />
      )}
    />,
    <Route
      key="coupon"
      path={`/${routes.containers.coupon}/:couponCode`}
      render={(props) => (
        <CouponDetail {...props} greetingSplashScreen={renderGreetingSplashScreenComponent()} />
      )}
    />,
    <Route
      key="deal"
      path={`/${routes.containers.deal}/:dealCode`}
      render={(props) => (
        <DealDetail {...props} greetingSplashScreen={renderGreetingSplashScreenComponent()} />
      )}
    />,
    <Route
      key="feedback"
      path={`/${routes.containers.feedback}`}
      render={(props) => (
        <Feedback
          {...props}
          greetingSplashScreen={renderGreetingSplashScreenComponent(routes.containers.feedback)}
        />
      )}
    />,
    <Route
      key="rewards-club"
      path={`/${routes.containers.rewardsClubSignup}`}
      render={(props) => (
        <RewardsClubSignup
          {...props}
          greetingSplashScreen={renderGreetingSplashScreenComponent()}
        />
      )}
    />,
    renderViewRoute({ View }),
    showPWAFeatureFlag && (
      <Route
        key="get-app"
        path={`/${routes.containers.getApp}`}
        render={(props) => (
          <DownloadApp
            {...props}
            handleInstallPWAClick={handleInstallPWAClick}
            enablePWAInstallWidgets={enablePWAInstallWidgets}
            isPWAInstalled={isPWAInstalled}
            pwaInstallUserChoice={pwaInstallUserChoice}
            isPWAInstallable={isPWAInstallable}
          />
        )}
      />
    ),
    renderOrderingStandaloneRoute(ctx),
    renderDefaultRoute(ctx),
  ].filter(Boolean);
};

/** Pick route set for Redux common.surface (FUDR-14577). */
export const renderRoutesForSurface = (surface, ctx) => {
  switch (surface) {
    case FUDR_SURFACES.SITE:
      return renderSiteSurfaceRoutes(ctx);
    case FUDR_SURFACES.APP:
      return renderAppSurfaceRoutes(ctx);
    case FUDR_SURFACES.ORDERING:
      return renderOrderingSurfaceRoutes(ctx);
    case FUDR_SURFACES.LEGACY:
    default:
      return renderLegacySurfaceRoutes(ctx);
  }
};
